import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext } from 'react';
import { ExerciseResult } from '../../@types/exercise-types';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';
import { ExerciseTranslationsContext } from '../../contexts/ExerciseTranslationsContext';
import ExerciseHighLightedItem from '../exercise-highlighted-item';
import ExerciseHighlightedItemTags from '../exercise-highlighted-item-tags';

interface LaunchModalPausedSessionProps {
  onResume?: () => void;
  onRestart?: () => void;
  result: ExerciseResult;
}

function LaunchModalPausedSession({ onResume, onRestart, result }: LaunchModalPausedSessionProps): JSX.Element | null {
  const translations = useContext(ExerciseTranslationsContext);

  const { medialink, module, onClose } = useExerciseManagerContext();

  return (
    <Dialog
      id="launch-modal-paused-session"
      modal
      onClose={onClose}
      aria-label={translations.launchModalPausedSession.a11yLabel}
    >
      <Dialog.Header
        title={translations.launchModalPausedSession.title}
        onClose={onClose}
        aria-label={translations.closeDialog}
      />
      <Dialog.Body>
        <div className="modal-content-text">
          {onRestart ? (
            <p>
              {translations.launchModalPausedSession.message.p1.restartable}
              <br /> {translations.launchModalPausedSession.message.p2}
            </p>
          ) : (
            <p>{translations.launchModalPausedSession.message.p1.nonRestartable}</p>
          )}
        </div>
        <ExerciseHighLightedItem
          status="busy"
          medialink={medialink}
          label={translations.launchModalPausedSession.highlightedItem.label}
          scoreTitle={translations.launchModalPausedSession.highlightedItem.score}
          percentageTitle={translations.launchModalPausedSession.highlightedItem.percentage}
          resultTitle={translations.launchModalPausedSession.highlightedItem.resultTitle}
          score={result.score}
          tags={{
            'aria-label': undefined,
            role: undefined,
            children: (
              <ExerciseHighlightedItemTags
                module={module}
                medialink={medialink}
                translations={translations.launchModalPausedSession.highlightedItem}
              />
            ),
          }}
          phases={result.phases}
          isLastPhase={result.isLastPhase}
        />
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={
          onRestart
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="neutral" onClick={onRestart}>
                    {translations.launchModalPausedSession.buttons.restart}
                  </Button>
                ),
              }
            : undefined
        }
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button onClick={onClose} prominence="neutral" key="cancel">
                {translations.launchModalPausedSession.buttons.cancel}
              </Button>
              {onResume && (
                <Button onClick={onResume} prominence="success" key="success">
                  {translations.launchModalPausedSession.buttons.resume}
                </Button>
              )}
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default LaunchModalPausedSession;
